/**
 * Adresses des écrans, dans le fragment de l'URL (#/paquets…) : de vrais liens, ouvrables
 * dans un nouvel onglet, qui marchent chez n'importe quel hébergeur statique.
 */
import { useEffect, useState } from 'react';

export type OngletReglagesPaquet = 'seance' | 'etapes' | 'planification';

export type Route =
  | { ecran: 'paquets'; paquet?: string }
  | { ecran: 'seance'; paquet: string }
  | { ecran: 'editeur'; carteMere?: string; paquet?: string }
  | { ecran: 'gestion'; paquet?: string; recherche?: string }
  | { ecran: 'statistiques'; paquet?: string }
  | { ecran: 'reglages-compte' }
  | { ecran: 'reglages-paquet'; paquet: string; onglet?: OngletReglagesPaquet };

export function lireRoute(fragment: string): Route {
  const [chemin = '', requete = ''] = fragment.replace(/^#\/?/, '').split('?');
  const parametres = new URLSearchParams(requete);
  const p = parametres.get('paquet') ?? undefined;
  const morceaux = chemin.split('/').filter(Boolean).map(decodeURIComponent);
  const avec = <T extends object>(route: T, cle: string, valeur: string | undefined): T =>
    valeur === undefined ? route : { ...route, [cle]: valeur };
  switch (morceaux[0]) {
    case 'seance':
      if (morceaux[1]) return { ecran: 'seance', paquet: morceaux[1] };
      break;
    case 'cartes':
      if (morceaux[1] === 'nouvelle' || morceaux[1] === undefined) {
        return avec({ ecran: 'editeur' }, 'paquet', p);
      }
      return { ecran: 'editeur', carteMere: morceaux[1] };
    case 'gestion':
      return avec(
        avec({ ecran: 'gestion' }, 'paquet', p),
        'recherche',
        parametres.get('q') ?? undefined,
      );
    case 'statistiques':
      return avec({ ecran: 'statistiques' }, 'paquet', p);
    case 'reglages':
      return { ecran: 'reglages-compte' };
    case 'paquets':
      if (morceaux[1] && morceaux[2] === 'reglages') {
        const onglet = morceaux[3];
        return onglet === 'etapes' || onglet === 'planification' || onglet === 'seance'
          ? { ecran: 'reglages-paquet', paquet: morceaux[1], onglet }
          : { ecran: 'reglages-paquet', paquet: morceaux[1] };
      }
      return avec({ ecran: 'paquets' }, 'paquet', morceaux[1]);
  }
  return { ecran: 'paquets' };
}

export function adresse(route: Route): string {
  const q = (parametres: Record<string, string | undefined>) => {
    const p = new URLSearchParams(
      Object.entries(parametres).filter((e): e is [string, string] => e[1] !== undefined),
    ).toString();
    return p ? `?${p}` : '';
  };
  const e = encodeURIComponent;
  switch (route.ecran) {
    case 'paquets':
      return route.paquet ? `#/paquets/${e(route.paquet)}` : '#/paquets';
    case 'seance':
      return `#/seance/${e(route.paquet)}`;
    case 'editeur':
      return route.carteMere
        ? `#/cartes/${e(route.carteMere)}`
        : `#/cartes/nouvelle${q({ paquet: route.paquet })}`;
    case 'gestion':
      return `#/gestion${q({ paquet: route.paquet, q: route.recherche })}`;
    case 'statistiques':
      return `#/statistiques${q({ paquet: route.paquet })}`;
    case 'reglages-compte':
      return '#/reglages';
    case 'reglages-paquet':
      return `#/paquets/${e(route.paquet)}/reglages${route.onglet ? `/${route.onglet}` : ''}`;
  }
}

export function naviguer(route: Route, remplacer = false) {
  const cible = adresse(route);
  if (remplacer) window.history.replaceState(null, '', cible);
  else window.history.pushState(null, '', cible);
  window.dispatchEvent(new HashChangeEvent('hashchange'));
}

export function useRoute(): Route {
  const [route, setRoute] = useState(() => lireRoute(window.location.hash));
  useEffect(() => {
    const ecouter = () => setRoute(lireRoute(window.location.hash));
    window.addEventListener('hashchange', ecouter);
    window.addEventListener('popstate', ecouter);
    return () => {
      window.removeEventListener('hashchange', ecouter);
      window.removeEventListener('popstate', ecouter);
    };
  }, []);
  return route;
}
